import { matchSorter } from 'match-sorter'

import { AccommodationDataWithRatePlans } from '../types/accommodation'
import { RATE_PLAN_SEARCH_KEYS } from '../types/rateplan'

/**
 * Filter accommodations for the rate-plan search box (Bulk Update +
 * contract EntitySelection).
 *
 * - Each accommodation's rate plans are narrowed with matchSorter over
 *   RATE_PLAN_SEARCH_KEYS. `code` is deliberately NOT among the keys: it
 *   does not exist on the RATEPLANS_QUERY results, and an explicit `keys`
 *   array makes matchSorter drop every item that misses all listed keys —
 *   the old key arrays therefore silently matched nothing on code search.
 * - An accommodation whose rate-plan list narrows to nothing is dropped
 *   entirely, instead of rendering an empty box.
 * - An accommodation whose own name matches the query is kept with its
 *   FULL rate-plan list: the user searched for the accommodation, not for
 *   plans inside it.
 */
export function filterAccommodationsByRatePlanQuery(
  accommodations: AccommodationDataWithRatePlans[],
  query: string
): AccommodationDataWithRatePlans[] {
  if (!query) {
    return [...accommodations]
  }

  const normalizedQuery = query.toLowerCase()

  const matchesAccommodationName = (
    acc: AccommodationDataWithRatePlans
  ): boolean => (acc.label || '').toLowerCase().includes(normalizedQuery)

  return accommodations
    .map((acc) => ({
      ...acc,
      ratePlans: matchesAccommodationName(acc)
        ? acc.ratePlans || []
        : matchSorter(acc.ratePlans || [], query, {
            keys: RATE_PLAN_SEARCH_KEYS
          })
    }))
    .filter(
      (acc) => matchesAccommodationName(acc) || (acc.ratePlans?.length ?? 0) > 0
    )
}
